import React, { useEffect, useState } from "react";

import {
  User,
  Users,
  FileCheck,
  Loader,
  CheckCircle2,
  ShieldCheck,
  Download,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { FaPlus } from "react-icons/fa";
import "../../styles/user/userdashboard.css";
import {
  fetchUserDashboardData,
  fetchRecentCandidates,
  fetchSubmittedCandidates,
  fetchPendingSubmissionCandidates,
  fetchReadyForBgcCandidates,
  startCandidateBGC,
  fetchFullCandidateReport,
  fetchNotifications,
  validateWalletBalance,
  downloadReport,
  fetchActivityFeed,
} from "../../services/user/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Dashboard = ({ setActiveMenuItem }) => {
  const [userData, setUserData] = useState(null);
  const [recentCandidates, setRecentCandidates] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const navigate = useNavigate();

  //  Notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const res = await fetchNotifications();
        if (res.data) {
          setMessages(res.data);
        }
      } catch (err) {}
    };
    const loadActivityFeed = async () => {
      try {
        const res = await fetchActivityFeed();
        setActivityFeed(res?.data?.data || []);
      } catch (err) {}
    };

    loadActivityFeed();
    loadNotifications();
  }, []);

  //  Dashboard stats
  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await fetchUserDashboardData();
        if (res.data && res.data.data) {
          setUserData(res.data.data);
        } else {
          throw new Error("Invalid data format from API");
        }
      } catch (err) {
        setError("Failed to fetch data. Please try again later.");
      }
    };
    loadData();
  }, []);

  //  Recent candidates
  useEffect(() => {
    const loadRecentCandidates = async () => {
      try {
        const res = await fetchRecentCandidates();
        if (res.data && res.data.content) {
          setRecentCandidates(res.data.content);
        } else {
          setRecentCandidates([]);
        }
      } catch (err) {}
    };
    loadRecentCandidates();
  }, []);

  //  Form submissions
  useEffect(() => {
    const loadFormSubmissions = async () => {
      try {
        const res = await fetchSubmittedCandidates(0, 10);
        const content = res?.data?.data?.content;
        if (Array.isArray(content)) {
          const updated = content.map((candidate) => ({
            ...candidate,
            bgcRequested: candidate.isStartBgc || false,
          }));
          setFormSubmissions(updated);
        } else {
          setFormSubmissions([]);
        }
      } catch (err) {}
    };
    loadFormSubmissions();
  }, []);

  // ▶️ Start BGC
  // const handleStartBGC = async (candidateId) => {
  //   try {
  //     await startCandidateBGC(candidateId);
  //     setFormSubmissions((prev) =>
  //       prev.map((form) =>
  //         form.candidateid === candidateId ? { ...form, bgcRequested: true } : form
  //       )
  //     );
  //     toast.success('BGC request sent successfully');
  //   } catch (err) {
  //     console.error('Failed to send BGC request:', err);
  //     toast.error('BGC request failed');
  //   }
  // };

  const handleStartBGC = async (candidateId) => {
    try {
      const clientId = Number(localStorage.getItem("clientId"));

      // Temporary
      // Later replace with selected checks from UI
      const selectedChecks = [{ checkId: 1 }, { checkId: 2 }];

      const request = {
        clientId,
        candidateId,
        selectedChecks,
      };

      // Validate Wallet
      const validateResponse = await validateWalletBalance(request);

      if (!validateResponse.data.canProceed) {
        toast.error(validateResponse.data.message);
        return;
      }

      // Start BGC
      await startCandidateBGC(candidateId);

      setFormSubmissions((prev) =>
        prev.map((form) =>
          form.candidateid === candidateId
            ? { ...form, bgcRequested: true }
            : form,
        ),
      );

      toast.success("BGC Started Successfully");
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || "Unable to start BGC");
    }
  };

  // 📑 Download Report
  const handleDownloadReport = async (candidateId, name = "Report") => {
    try {
      const response = await downloadReport(candidateId);
      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `${name.replace(/\s+/g, "_")}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
      toast.success("Report downloaded");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  // 📜 View Full Report (API check only, not navigation yet)
  const handleViewFullReport = async (candidateId) => {
    try {
      const res = await fetchFullCandidateReport(candidateId);
    } catch (err) {
      toast.error("Failed to fetch candidate report");
    }
  };

  // ➕ Add Candidate
  const handleAddCandidate = () => {
    setActiveMenuItem("Add Candidate");
  };

  if (error) return <div className="loading error">{error}</div>;
  if (!userData)
    return (
      <div className="centered-message">
        <div className="spinner"></div>
        <div>Loading dashboard...</div>
      </div>
    );

  const stats = [
    {
      title: "Total Candidates",
      count: userData.totalApplicants || 0,
      icon: <User size={32} color="#4f46e5" />,
    },
    {
      title: "Pending Submission",
      count: userData.pendingSubmission || 0,
      icon: <FileCheck size={32} color="#f97316" />,
    },
    {
      title: "Pending Case Submission",
      count: userData.pendingCasesSubmission || 0,
      icon: <Loader size={32} color="#eab308" />,
    },
    {
      title: "Ready for BGC",
      count: userData.readyForBgcCount || 0,
      icon: <FileCheck size={32} color="#3b82f6" />,
    },
    {
      title: "Submitted for Verification",
      count: userData.submittedForVerification || 0,
      icon: <ShieldCheck size={32} color="#0ea5e9" />,
    },
    {
      title: "Login Expired",
      count: userData.loginExpiredCount || 0,
      icon: <Users size={32} color="#ef4444" />,
    },
    {
      title: "BGC Completed",
      count: userData.bgvCompletedCount || 0,
      icon: <CheckCircle2 size={32} color="#10b981" />,
    },
  ];

  const getIconBgColor = (index) => {
    const bgColors = [
      "#e0e7ff",
      "#ffedd5",
      "#fef9c3",
      "#e0f2fe",
      "#fee2e2",
      "#d1fae5",
    ];
    return bgColors[index % bgColors.length];
  };

  const pieChartData = [
    { name: "Total Candidates", value: userData.totalApplicants || 0 },
    { name: "Pending Submission", value: userData.pendingSubmission || 0 },
    {
      name: "Pending Case Submission",
      value: userData.pendingCasesSubmission || 0,
    },
    { name: "Ready for BGC", value: userData.readyForBgcCount || 0 },
    {
      name: "Submitted for Verification",
      value: userData.submittedForVerification || 0,
    },
    {
      name: "Login Expired Candidates",
      value: userData.loginExpiredCount || 0,
    },
    { name: "Completed Verification", value: userData.bgvCompletedCount || 0 },
  ];

  const PIE_COLORS = [
    "#7E0090",
    "#FF7C00",
    "#FFC800",
    "#00CCFF",
    "#0096FF",
    "#009600",
  ];

  return (
    <div className="client-dashboard-container">
      {/* Header */}
      <div className="client-dashboard-header">
        <h1>Welcome back</h1>
        <button
          className="client-add-candidate-btn"
          onClick={handleAddCandidate}
        >
          <FaPlus /> Add Candidate
        </button>
      </div>

      {/* Stats Cards */}
      <div className="client-cards-wrapper">
        {stats.map((stat, index) => {
          const menuMap = {
            "Total Candidates": "Total Candidates",
            "Pending Submission": "Pending Submission",
            "Pending Case Submission": "Pending Case Submission",
            "Ready for BGC": "Ready for BGC",
            "Submitted for Verification": "Submitted Verification",
            "Login Expired": "Login Expired Candidates",
            "BGC Completed": "BGV Completed Candidates",
          };
          return (
            <div
              className="client-card clickable"
              key={index}
              onClick={() => setActiveMenuItem(menuMap[stat.title])}
            >
              <p className="client-card-title">{stat.title}</p>
              <div className="client-icon-cout-cont">
                <p className="client-card-count">{stat.count}</p>
                <div
                  className="client-icon-wrapper"
                  style={{ backgroundColor: getIconBgColor(index) }}
                >
                  {stat.icon}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="client-main-content">
        {/* Pie Chart */}
        <div className="client-card client-pie-section">
          <h2>Overall Candidate Status</h2>
          <div className="client-pie-flex">
            <ResponsiveContainer width="55%" height={200}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  dataKey="value"
                  paddingAngle={5}
                  labelLine={false}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="client-legend">
              {pieChartData.map((entry, index) => (
                <div key={index} className="client-legend-item">
                  <span
                    style={{
                      backgroundColor: PIE_COLORS[index],
                      width: 15,
                      height: 12,
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: 6,
                    }}
                  ></span>
                  {entry.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="client-card client-user-activity-feed">
          <h2>Activity Feed</h2>

          <div className="client-list-wrapper">
            {activityFeed.length > 0 ? (
              activityFeed.map((activity, idx) => (
                <div key={idx} className="client-activity-card">
                  <div className="client-activity-header ">
                    <div className="client-activity-content">
                      {/* <h4 className="client-activity-title">
                        {activity.activityType}
                      </h4> */}

                      <p className="client-activity-message">
                        {activity.description}
                      </p>

                      <div className="client-activity-footer">
                        <small className="client-activity-date">
                          {activity.createdAt
                            ? new Date(activity.createdAt).toLocaleString(
                                "en-IN",
                                {
                                  timeZone: "Asia/Kolkata",
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )
                            : ""}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="client-no-activity">No recent activity yet.</p>
            )}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="client-bottom-section">
          {/* Recent Candidates */}
          <div className="client-user-card">
            <h2>Recent Candidates</h2>
            <div className="client-list-wrapper">
              {recentCandidates.length > 0 ? (
                recentCandidates.map((candidate, index) => (
                  <div key={index} className="client-list-item">
                    {/* <img
                      className='client-candidate-image'
                      src='https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRBwgu1A5zgPSvfE83nurkuzNEoXs9DMNr8Ww&s'
                      alt='static_img'
                    /> */}
                    <div>
                      <p className="client-candidate-user-name">
                        {candidate.fullName}
                      </p>
                      <span className="client-candidate-user-date">
                        {" "}
                        {candidate.caseInitiatedDateOnly}
                      </span>
                    </div>
                    <span
                      className={`client-report-status ${candidate.bgcStatus?.toLowerCase()}`}
                    >
                      {candidate.bgcStatus}
                    </span>
                  </div>
                ))
              ) : (
                <p>No recent candidates found.</p>
              )}
            </div>
          </div>

          {/* Form Submissions */}
          <div className="client-user-card">
            <h2>Form Submissions</h2>
            <div className="client-list-wrapper">
              {formSubmissions.length > 0 ? (
                formSubmissions.map((form, index) => (
                  <div key={index} className="client-list-item">
                    {/* <img
                      className='client-candidate-image'
                      src='https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRBwgu1A5zgPSvfE83nurkuzNEoXs9DMNr8Ww&s'
                      alt='static_img'
                    /> */}
                    <div>
                      <p className="client-candidate-user-name">
                        {form.fullname}
                      </p>
                      <span className="client-candidate-user-date">
                        {" "}
                        {form.caseInitiatedDateOnly}
                      </span>
                    </div>
                    <button
                      className={`client-status-btn ${form.bgcRequested ? "bg-green-600 text-white" : "bg-purple-600 text-white"}`}
                      onClick={() => handleStartBGC(form.candidateid)}
                      disabled={form.bgcRequested}
                    >
                      {form.bgcRequested ? "Request Sent" : "Request BGC"}
                    </button>
                  </div>
                ))
              ) : (
                <p>No submissions found.</p>
              )}
            </div>
          </div>

          {/* Reports */}
          <div className="client-user-card">
            <h2>Reports</h2>
            <div className="client-list-wrapper">
              {formSubmissions.length > 0 ? (
                formSubmissions.map((form, index) => (
                  <div key={index} className="client-list-item">
                    {/* <img
                      className='client-candidate-image'
                      src='https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRBwgu1A5zgPSvfE83nurkuzNEoXs9DMNr8Ww&s'
                      alt='static_img'
                    /> */}
                    <div>
                      <p className="client-candidate-user-name">
                        {form.fullname}
                      </p>
                      <span className="client-candidate-user-date">
                        {" "}
                        {form.caseInitiatedDateOnly}
                      </span>
                    </div>
                    <p className="client-download-status">completed</p>
                  </div>
                ))
              ) : (
                <p>No reports found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
